import { getDocuments } from "./documents";
import type { RetrievalHit, StoredDocument } from "./types";

/** Dot product of two Float32Arrays of equal length. */
function dot(a: Float32Array, b: Float32Array): number {
  let s = 0;
  for (let i = 0; i < a.length; i++) s += a[i] * b[i];
  return s;
}

function norm(a: Float32Array): number {
  return Math.sqrt(dot(a, a));
}

export function cosine(a: Float32Array, b: Float32Array): number {
  if (a.length !== b.length) {
    throw new Error(
      `cosine: vectors have different lengths (${a.length} vs ${b.length})`,
    );
  }
  const denom = norm(a) * norm(b);
  if (denom === 0) return 0;
  return dot(a, b) / denom;
}

export interface RetrieveOptions {
  /** Top-K to return. */
  k?: number;
  /** If set, only return hits with score >= threshold. */
  minScore?: number;
}

/** Brute-force cosine retrieval across all chunks of the given documents. */
export async function retrieve(
  queryEmbedding: Float32Array,
  activeDocIds: string[],
  options: RetrieveOptions = {},
): Promise<RetrievalHit[]> {
  const { k = 8, minScore } = options;
  if (activeDocIds.length === 0) return [];

  const docs = await getDocuments(activeDocIds);
  return retrieveFromDocs(queryEmbedding, docs, { k, minScore });
}

/** Same as retrieve() but on in-memory documents — used by tests. */
export function retrieveFromDocs(
  queryEmbedding: Float32Array,
  docs: StoredDocument[],
  options: RetrieveOptions = {},
): RetrievalHit[] {
  const { k = 8, minScore } = options;
  const hits: RetrievalHit[] = [];

  for (const doc of docs) {
    for (const chunk of doc.chunks) {
      const score = cosine(queryEmbedding, chunk.embedding);
      if (minScore !== undefined && score < minScore) continue;
      hits.push({ docId: doc.id, docName: doc.name, chunk, score });
    }
  }

  hits.sort((a, b) => b.score - a.score);
  return hits.slice(0, k);
}
